// filepath: apps/web/src/features/observability/types.ts
//
// FR-UI-DASH-1 -- TypeScript wire-type mirrors for the tenant
// observability dashboard surface.
//
// Wire shapes mirror the Go server contracts at:
//   - GET  /api/metrics/summary
//          -> TenantMetricsSummary
//          (internal/obs/metrics_handler.go:TenantMetricsResponse)
//   - GET  /api/metrics/instances-by-process-definition
//          -> ProcessDefinitionBreakdown
//          (internal/obs/process_definition_breakdown_handler.go:
//           ProcessDefinitionBreakdownResponse -- ISS-f4fa1b7b-268)
//   - GET  /api/observability/alerts/active
//          -> ActiveAlert[]   (FR-OBS-4 -- endpoint not yet shipped)
//   - POST /api/observability/alerts/rules
//          -> AlertRulePayload
//          (FR-OBS-4 -- endpoint not yet shipped; v1 SPA tolerates
//           404/501 with typed envelope shape -- mirroring the
//           FR-UI-ADM-12 ISS-f4fa1b7b-250 precedent).
//
// SECURITY / INTEGRITY INVARIANTS:
//   - INV-1: the SPA renders exactly what the server returns. The
//     dashboard is read-only; no client-side data shaping. The
//     tenantSlug flows into every TanStack Query cache key as the
//     2nd segment (see `observabilityKeys` below) -- the server
//     filter is the only security boundary.
//   - INV-2: field-level RBAC stripping is server-side. If a field
//     is unauthorised for the caller, the server omits it and the
//     SPA renders nothing for that field -- never client-side
//     filtering as a security boundary.
//   - INV-5: no secret values appear anywhere in these types.
//     `AlertRulePayload` carries the typed expression source string
//     only -- the alert rule's auth credentials (HMAC keys, etc.)
//     are server-managed (FR-OBS-4) and never cross the wire in
//     this surface. The static guard in
//     apps/web/src/runtime/staticGuards.test.ts scans this directory
//     for canonical INV-5 patterns.
//   - INV-9: every server-supplied string renders as a text node.
//     Zero `dangerouslySetInnerHTML` anywhere in this subtree.
//   - FR-UI-NFR-2: no client-side eval. The FormulaEditor widget
//     (composed by AlertRuleAuthoringDialog) is the only formula-
//     authoring surface; it talks to the server via
//     POST /api/formula/validate (FR-VAL-4). The dashboard never
//     parses or executes expressions.

/**
 * MetricId -- typed enum for drill-down navigation. Mirrors the
 * FR-OBS-3 metric categories. The MetricId is URL-encoded into the
 * drill-down query string (see DrilldownLink.tsx) -- using a typed
 * enum prevents injection of arbitrary query values.
 */
export type MetricId =
  | "instance_count"
  | "error_rate"
  | "step_duration_p50"
  | "step_duration_p95"
  | "step_duration_p99"
  | "process_definition_breakdown";

/** All MetricId values -- used for runtime validation in DrilldownLink
 *  (asserts that the metric query-param is one of the typed values). */
export const METRIC_IDS = [
  "instance_count",
  "error_rate",
  "step_duration_p50",
  "step_duration_p95",
  "step_duration_p99",
  "process_definition_breakdown",
] as const;

/**
 * MetricKPI -- a single KPI tile value + chrome.
 * Used by InstanceCountWidget (AC-1 widget 1) and the
 * StepDurationHistogram (AC-1 widget 3).
 */
export interface MetricKPI {
  /** Stable metric identifier. */
  metric_id: MetricId;
  /** Localized chrome label (i18n key path; rendered via t()). */
  label_key: string;
  /** Numeric value (instance count, p50 ms, p95 ms, etc.). */
  value: number;
  /** Unit string (ms, %, count) -- rendered verbatim next to the value. */
  unit: string;
  /** Optional secondary caption (e.g. "last 24h"). */
  caption_key?: string;
}

/**
 * ErrorRatePoint -- one per-hour data point in the
 * ErrorRateSparkline (AC-1 widget 2). Time-ordered ascending.
 */
export interface ErrorRatePoint {
  /** ISO-8601 timestamp for the hour bucket. */
  bucket_at: string;
  /** Number of step errors in the bucket. */
  step_error_count: number;
  /** Total number of steps in the bucket. */
  step_count: number;
  /** Computed error rate percentage (0-100). */
  error_rate_pct: number;
}

/**
 * DurationHistogram -- p50/p95/p99 buckets for the
 * StepDurationHistogram (AC-1 widget 3). The server returns the
 * canonical three values; the SPA renders them as horizontal bars.
 */
export interface DurationHistogram {
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  /** Total step count for context (optional -- may be omitted in v1). */
  step_count?: number;
}

/**
 * ActiveAlert -- one entry in the Active Alerts panel (AC-3).
 * The endpoint does not yet exist (FR-OBS-4 not shipped); the SPA
 * renders the typed empty state `data-state="awaiting_fr_obs_4"`
 * when the panel receives an empty array. When FR-OBS-4 ships,
 * the same type renders populated alerts.
 */
export interface ActiveAlert {
  alert_id: string;
  /** Rule name (server-emitted, rendered verbatim). */
  rule_name: string;
  /** ISO-8601 timestamp the alert started firing. */
  firing_since: string;
  /** ISO-8601 timestamp the alert was last evaluated. */
  last_evaluated_at: string;
  /** Severity -- one of "info" | "warning" | "critical". */
  severity: "info" | "warning" | "critical";
  /** Human-readable summary -- rendered as a text node (INV-9). */
  summary: string;
}

/**
 * TenantMetricsSummary -- the full wire envelope returned by
 * GET /api/metrics/summary. Mirrors
 * internal/obs/metrics_handler.go:TenantMetricsResponse.
 *
 * Field set is the FR-OBS-3 v1 surface:
 *   - instance_count + error_count        (AC-1 widget 1)
 *   - step_count + step_error_count      (AC-1 widget 2 sparkline)
 *   - step_duration p50/p95/p99          (AC-1 widget 3 histogram)
 *   - effect_http_* + effect_email_*     (not surfaced in v1 dashboard;
 *                                          reserved for FR-OBS-3
 *                                          extension widgets; SPA
 *                                          tolerates their absence)
 *
 * INV-1: server filters by tenant_id before serialization.
 * INV-2: server strips any unauthorised fields before serialization.
 */
export interface TenantMetricsSummary {
  tenant_id: string;
  slug: string;
  instance_count: number;
  error_count: number;
  active_sandbox_count: number;
  step_count: number;
  step_error_count: number;
  step_duration_buckets: number[];
  /** p50/p95/p99 latency in milliseconds. */
  step_duration_p50_ms: number;
  step_duration_p95_ms: number;
  step_duration_p99_ms: number;
  effect_http_success: number;
  effect_http_retry: number;
  effect_http_dead_letter: number;
  effect_http_latency_p50: number;
  effect_http_latency_p95: number;
  effect_http_latency_p99: number;
  effect_email_success: number;
  effect_email_retry: number;
  effect_email_dead_letter: number;
  effect_email_latency_p50: number;
  effect_email_latency_p95: number;
  effect_email_latency_p99: number;
  computed_at: string;
  /**
   * Per-hour error rate sparkline data (AC-1 widget 2). May be
   * empty when the tenant has no recent step activity. When absent
   * (defensive normaliser), the widget renders an empty-state
   * chrome ("no recent step activity").
   */
  error_rate_sparkline?: ErrorRatePoint[];
}

/**
 * ProcessDefinitionBreakdownRow -- one per-process_definition_id
 * aggregate row (AC-1 widget 4, ISS-f4fa1b7b-268).
 *
 * `name` is the pinned definition version's name (server-side PK
 * join -- no "latest version" folding; two versions of the same
 * name are two rows disambiguated by the id).
 *
 * INV-9: `name` is a server-supplied string rendered as a text
 * node by the widget. INV-1: rows are already tenant-filtered
 * server-side before serialization.
 */
export interface ProcessDefinitionBreakdownRow {
  /** UUID of the pinned process definition version row. */
  process_definition_id: string;
  /** Definition name (server-emitted, rendered verbatim). */
  name: string;
  /** Total instances of this definition (all states). */
  instance_count: number;
  /** Instances in state 'failed' (cancelled excluded). */
  failed_count: number;
}

/**
 * ProcessDefinitionBreakdown -- the wire envelope returned by
 * GET /api/metrics/instances-by-process-definition. Mirrors
 * internal/obs/process_definition_breakdown_handler.go:
 * ProcessDefinitionBreakdownResponse. Empty tenant -> `rows: []`
 * (empty array, never null).
 */
export interface ProcessDefinitionBreakdown {
  rows: ProcessDefinitionBreakdownRow[];
}

/**
 * AlertRulePayload -- the body shape for
 * POST /api/observability/alerts/rules (AC-2 Save target).
 *
 * INV-5 contract: the `expression` is the typed expression source
 * string only -- never the resolved value, never secret material.
 * The alert rule's HMAC signing key (if applicable in FR-OBS-4) is
 * server-managed; the SPA never carries it on this surface.
 */
export interface AlertRulePayload {
  /** Stable client-side rule id (UUID v4). Used for idempotent
   *  POST; the server may overwrite. */
  rule_id: string;
  /** Human-readable rule name. */
  rule_name: string;
  /** The typed expression source string (expr-lang). */
  expression: string;
  /** Severity threshold for the rule firing. */
  severity: "info" | "warning" | "critical";
  /** Optional description / rationale. */
  description?: string;
}

/**
 * AlertRulePersistError -- typed envelope returned by POST
 * /api/observability/alerts/rules when the endpoint does not yet
 * exist (FR-OBS-4 not shipped). Mirrors the FR-UI-ADM-12
 * ISS-f4fa1b7b-250 precedent for typed not-implemented envelopes.
 *
 * The SPA recognises this envelope in AlertRuleAuthoringDialog
 * and renders the InlineFieldError chrome with text
 * "alert-rule persistence will ship with FR-OBS-4" -- preserving
 * the validated source string verbatim (AC-2a).
 */
export interface AlertRulePersistError {
  code: "not_implemented" | "fr_obs_4_pending";
  message: string;
  /** Server-supplied fallback hint (optional). */
  hint?: string;
}

/**
 * TanStack Query cache-key factory for observability queries.
 *
 * INV-1: tenantSlug is the 2nd segment of every emitted key,
 * mirroring the FR-UI-PROC-1 `procKeys` and FR-UI-ADM-7
 * `webhookKeyKeys` patterns. Switching tenants does NOT leak the
 * prior tenant's dashboard data -- the cache key changes, the
 * query refetches.
 */
export const observabilityKeys = {
  all: ["observability"] as const,
  /** Per-tenant metrics summary key (AC-1 widgets 1-3). */
  metricsSummary: (tenantSlug: string) =>
    ["observability", tenantSlug, "metrics-summary"] as const,
  /** Per-tenant active-alerts panel key (AC-3). */
  activeAlerts: (tenantSlug: string) =>
    ["observability", tenantSlug, "active-alerts"] as const,
  /** Per-tenant process-definition breakdown key (AC-1 widget 4,
   *  ISS-f4fa1b7b-268). */
  processBreakdown: (tenantSlug: string) =>
    ["observability", tenantSlug, "process-breakdown"] as const,
};
